import { HTTP } from "@/core/http";
import config from "@/config";

// Server types (move interfaces here or to server-types.ts)
export interface User {
  id?: number;
  _id?: string;
  name?: string;
  email?: string;
  role?: string;
  roles?: string[];
  status?: string;
  permissions?: string[];
  skills?: PlatformUserSkill[];
  [key: string]: unknown;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token?: string;
  user?: User;
  [key: string]: unknown;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface RegisterResponse {
  user?: User;
  message?: string;
  [key: string]: unknown;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  user_id: number;
}

export interface UpdatePasswordRequest {
  current_password: string;
  new_password: string;
  user_id: number;
}

/** GET /platform/tenant/resolve */
export interface ResolveTenantResponse {
  tenantCode: string;
  tenantName: string;
  uiUrl: string;
}

export interface ResolvedTenant {
  /** Value sent as x-tenantid, e.g. mira_10001 */
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  uiUrl: string;
  meta: ResolveTenantResponse;
}

/** Header tenant id: mira_{tenantCode} */
export function toTenantHeaderId(tenantCode: string): string {
  const code = tenantCode.trim();
  if (!code) return code;
  return code.startsWith("mira_") ? code : `mira_${code}`;
}

/** Role assignment from GET /platform/user-context. */
export interface PlatformUserRole {
  roleId: number;
  roleName: string;
  roleStatus: string;
}

/** Skill assignment from GET /platform/user-context (when present). */
export interface PlatformUserSkill {
  skillId?: number;
  skillName?: string;
  proficiencyLevel?: string;
  linkStatus?: string;
}

/** Wire response from GET /platform/user-context. */
export interface PlatformUserContextResponse {
  userId: number;
  name: string;
  email: string;
  status: string;
  roles: PlatformUserRole[];
  skills: PlatformUserSkill[];
  permissions: string[];
}

/** Normalized app context derived from {@link PlatformUserContextResponse}. */
export type PlatformUserContext = PlatformUserContextResponse;

export class AuthService {
  constructor(private readonly httpClient: HTTP) {}

  /**
   * GET /platform/user-context — user id, profile, and roles for the logged-in user.
   */
  async getUserContext(): Promise<PlatformUserContext> {
    return this.httpClient.get<PlatformUserContextResponse>(
      "/platform/user-context",
    );
  }

  /**
   * Resolve current tenant from Platform Service (requires bearer token).
   * Call after Cognito sign-in / when a session token is already stored.
   * Locally may pass ui_url so QA can map without Origin spoofing.
   */
  async resolveTenant(): Promise<ResolvedTenant> {
    const uiUrl =
      import.meta.env.VITE_TENANT_UI_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");

    const response = await this.httpClient.get<ResolveTenantResponse>(
      config.ENDPOINTS.AUTH.RESOLVE_TENANT,
      {
        ...(uiUrl ? { params: { ui_url: uiUrl } } : {}),
      }
    );

    const tenantCode =
      typeof response.tenantCode === "string" ? response.tenantCode.trim() : "";

    if (!tenantCode) {
      throw new Error("Unable to resolve tenant.");
    }

    const tenantId = toTenantHeaderId(tenantCode);
    const tenantName =
      typeof response.tenantName === "string" && response.tenantName.trim()
        ? response.tenantName.trim()
        : tenantCode;
    const resolvedUiUrl =
      typeof response.uiUrl === "string" ? response.uiUrl : uiUrl;

    return {
      tenantId,
      tenantCode,
      tenantName,
      uiUrl: resolvedUiUrl,
      meta: response,
    };
  }

  /**
   * Login user (legacy backend JWT — prefer Cognito for sign-in UI)
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    return this.httpClient.post<LoginResponse, LoginRequest>("/auth/login", {
      body: data,
    });
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return this.httpClient.post<RegisterResponse, RegisterRequest>(
      "/auth/sign-up",
      {
        body: data,
      }
    );
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<{ user: User }> {
    return this.httpClient.put<{ user: User }, UpdateProfileRequest>(
      "/auth/profile",
      {
        body: data,
      }
    );
  }

  /**
   * Update user password
   */
  async updatePassword(
    data: UpdatePasswordRequest
  ): Promise<{ message: string }> {
    return this.httpClient.put<{ message: string }, UpdatePasswordRequest>(
      "/auth/password",
      {
        body: data,
      }
    );
  }
}
